import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, AlertCircle, Save, Send, Loader2 } from "lucide-react";
import { resolveBackendBase } from "../lib/api";
import TAExecutionPage from "./ta/TAExecutionPage";
import HOSExecutionPage from "./hos/HOSExecutionPage";

const API_URL = `${resolveBackendBase()}/api/v2`;

export default function CandidateAssessmentPage() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [responses, setResponses] = useState({});
    const [savingState, setSavingState] = useState("idle"); // idle | saving | saved | error
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [isTA43, setIsTA43] = useState(false);
    const [isHOS43, setIsHOS43] = useState(false);

    useEffect(() => {
        axios.get(`${API_URL}/assessment/${token}`)
            .then((r) => {
                const meta = r.data?.template?.content?.metadata || {};
                if (meta.assessment_type === "technical_assessment" && String(meta.version) === "4.3") {
                    setIsTA43(true);
                    setLoading(false);
                    return;
                }
                if (meta.assessment_type === "hands_on_simulation" && String(meta.version) === "4.3") {
                    setIsHOS43(true);
                    setLoading(false);
                    return;
                }
                if (r.data.status === "completed") {
                    setSubmitted(true);
                } else {
                    setData(r.data);
                    setResponses(r.data.saved_responses || {});
                }
                setLoading(false);
            })
            .catch((e) => { setError(e.response?.data?.detail || "Failed to load assessment"); setLoading(false); });
    }, [token]);

    // Auto-save debounced
    useEffect(() => {
        if (!data || submitted) return;
        const progress = calcProgress(responses, data);
        const t = setTimeout(async () => {
            if (Object.keys(responses).length === 0) return;
            setSavingState("saving");
            try {
                await axios.post(`${API_URL}/assessment/${token}/save`, { responses, progress_pct: progress });
                setSavingState("saved");
                setTimeout(() => setSavingState("idle"), 1500);
            } catch {
                setSavingState("error");
            }
        }, 1500);
        return () => clearTimeout(t);
    }, [responses]);

    const calcProgress = useCallback((resps, tpl) => {
        if (!tpl?.template?.content?.modules) return 0;
        let total = 0;
        tpl.template.content.modules.forEach((m) => (m.sections || []).forEach((s) => { total += (s.questions || []).length; }));
        if (total === 0) return 0;
        const answered = Object.values(resps).filter((v) => v !== null && v !== undefined && v !== "").length;
        return Math.min(100, Math.round((answered / total) * 100));
    }, []);

    const setAnswer = (qid, val) => setResponses((r) => ({ ...r, [qid]: val }));

    const submit = async () => {
        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/assessment/${token}/submit`, { responses, progress_pct: 100 });
            setSubmitted(true);
        } catch (e) {
            alert("Submit failed: " + (e.response?.data?.detail || e.message));
        }
        setSubmitting(false);
    };

    if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-6 h-6 text-white animate-spin" /></div>;

    if (isTA43) return <TAExecutionPage />;
    if (isHOS43) return <HOSExecutionPage />;

    if (error) return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 p-8 max-w-md text-center">
                <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
                <h1 className="text-lg font-display font-bold text-white mb-2">Unable to load assessment</h1>
                <p className="text-sm text-gray-500">{error}</p>
            </div>
        </div>
    );

    if (submitted) return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
            <div className="bg-card border border-border/40 p-10 max-w-md text-center" data-testid="assessment-submitted">
                <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <h1 className="text-xl font-display font-bold text-white mb-2">Assessment submitted</h1>
                <p className="text-sm text-gray-500">Thank you. Your responses have been recorded. Your manager and HR partner will follow up.</p>
            </div>
        </div>
    );

    const modules = data?.template?.content?.modules || [];
    const progress = calcProgress(responses, data);

    return (
        <div className="min-h-screen bg-background" data-testid="assessment-take-page">
            {/* Header bar */}
            <header className="border-b border-border/40 bg-card sticky top-0 z-10">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div>
                        <h1 className="text-sm font-display font-bold text-white">Realloc Assessment</h1>
                        <p className="text-xs text-gray-600 font-mono">{data?.template?.job_title} &middot; {data?.candidate?.name}</p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="text-xs text-gray-500 font-mono">{progress}% complete</div>
                        {savingState === "saving" && <div className="text-xs text-gray-600 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving</div>}
                        {savingState === "saved" && <div className="text-xs text-green-400 flex items-center gap-1"><Save className="w-3 h-3" /> Saved</div>}
                    </div>
                </div>
                <div className="h-0.5 bg-border/40">
                    <div className="h-full bg-white transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                <div className="mb-8">
                    <h2 className="text-2xl font-display font-bold text-white mb-2">Welcome, {data?.candidate?.name?.split(" ")[0] || "there"}</h2>
                    {(() => {
                        const jt = data?.template?.job_title || "";
                        const moduleHints = (modules || []).map(m => (m.module_name || "")).join(" ").toLowerCase();
                        const isMV = /manager.?validation/i.test(jt) || /manager.?validation/i.test(moduleHints);
                        const isPeer = /peer.?review/i.test(jt);
                        const isSelf = /self.?assessment/i.test(jt);
                        // DB-sourced subject name (canonical). Falls back to filename regex.
                        let subjectName = data?.subject_candidate?.name || null;
                        if (!subjectName && isMV) {
                            const m = jt.match(/Manager[_\s-]+Validation[_\s-]+([A-Z][A-Za-z'-]+)[_\s-]+([A-Z][A-Za-z'-]+)/i);
                            if (m) subjectName = `${m[1]} ${m[2]}`.replace(/_/g, " ");
                        }
                        const subjectFirstName = subjectName ? subjectName.split(" ")[0] : null;

                        if (isMV) {
                            return (
                                <div className="text-sm text-gray-400 leading-relaxed space-y-3" data-testid="mv-welcome-copy">
                                    <p>
                                        You've been asked to complete the Manager Validation for{" "}
                                        <span className="text-white font-semibold">{subjectName || "this team member"}</span>. Your direct experience working with them is the most valuable input we can collect on how they show up day-to-day.
                                    </p>
                                    <p>
                                        Please rate them honestly on each dimension and back your scores with a short rationale — even one or two sentences materially improves the quality of the feedback they receive. There are no right or wrong answers; we're looking for your candid perspective.
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Your responses are reviewed only by authorized HR partners and are never shared verbatim with{" "}
                                        <span className="text-gray-300">{subjectFirstName || "the person being assessed"}</span>. Auto-saves every 1.5 seconds — you can close this tab and resume any time before submitting.
                                    </p>
                                </div>
                            );
                        }
                        if (isPeer && subjectName) {
                            return (
                                <div className="text-sm text-gray-400 leading-relaxed space-y-3" data-testid="peer-welcome-copy">
                                    <p>
                                        You've been asked to provide peer feedback on{" "}
                                        <span className="text-white font-semibold">{subjectName}</span>. As someone who works alongside them, your observations carry a kind of weight no manager review can replicate.
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Your responses stay confidential — authorized HR partners review them, and they're never shared verbatim with {subjectFirstName}. Auto-saves every 1.5 seconds.
                                    </p>
                                </div>
                            );
                        }
                        if (isSelf) {
                            return (
                                <div className="text-sm text-gray-400 leading-relaxed space-y-3" data-testid="self-welcome-copy">
                                    <p>
                                        Welcome to your self-assessment. This is your space to reflect candidly on how your role has evolved, what you've learned, and where you want to grow next.
                                    </p>
                                    <p className="text-xs text-gray-500">
                                        Your responses are reviewed only by authorized HR partners and are never used to attribute questions back to you. Auto-saves every 1.5 seconds.
                                    </p>
                                </div>
                            );
                        }
                        return (
                            <p className="text-sm text-gray-400 leading-relaxed">
                                This assessment helps us understand how your role is evolving. Your answers are reviewed only by authorized HR partners and never used to attribute questions back to you. Auto-saves every 1.5 seconds.
                            </p>
                        );
                    })()}
                </div>

                {modules.length === 0 && (
                    <div className="bg-card border border-border/40 p-8 text-sm text-gray-500 text-center">
                        This assessment hasn't been fully generated yet. Please check back with your HR partner.
                    </div>
                )}

                {modules.map((mod, mi) => (
                    <section key={mi} className="mb-10">
                        <h3 className="text-lg font-display font-bold text-white mb-1">{mod.module_name}</h3>
                        <p className="text-xs text-gray-600 font-mono mb-4">respondent: {mod.respondent}</p>
                        {(mod.sections || []).map((sec, si) => (
                            <div key={si} className="bg-card border border-border/40 p-5 mb-3">
                                <h4 className="text-sm font-semibold text-white mb-1">{sec.section_title}</h4>
                                <p className="text-xs text-gray-500 mb-4 leading-relaxed">{sec.instructions}</p>
                                <div className="space-y-5">
                                    {(sec.questions || []).map((q) => (
                                        <QuestionRenderer key={q.id} q={q} value={responses[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>
                ))}

                {modules.length > 0 && (
                    <div className="sticky bottom-6 bg-card border border-border/40 p-4 flex items-center justify-between">
                        <p className="text-xs text-gray-500">Review your responses before submitting. You can return later — we auto-save.</p>
                        <button onClick={submit} disabled={submitting}
                            className="px-5 py-2.5 bg-white text-black text-sm font-semibold hover:bg-gray-300 disabled:opacity-50 flex items-center gap-2" data-testid="submit-assessment-btn">
                            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting</> : <><Send className="w-4 h-4" /> Submit assessment</>}
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
}


function QuestionRenderer({ q, value, onChange }) {
    const common = "w-full bg-muted border border-border/40 px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-white/30 outline-none";
    return (
        <div data-testid={`question-${q.id}`}>
            <label className="block text-sm text-gray-200 mb-2 leading-relaxed">{q.prompt}</label>
            {q.type === "text" || q.type === "scenario_with_followup" ? (
                <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${common} h-28 resize-none`} placeholder="Your response..." />
            ) : q.type === "likert_5" ? (
                <div className="flex gap-2">
                    {["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"].map((label, i) => (
                        <button key={i} onClick={() => onChange(i + 1)}
                            className={`flex-1 px-2 py-2 text-xs border transition-colors ${value === i + 1 ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white hover:border-white/30"}`}>
                            {label}
                        </button>
                    ))}
                </div>
            ) : q.type === "single_choice" ? (
                <div className="flex flex-wrap gap-2" data-testid={`single-choice-${q.id}`}>
                    {(q.options || []).map((opt) => (
                        <button key={opt} onClick={() => onChange(opt)}
                            className={`px-3 py-1.5 text-xs border transition-colors ${value === opt ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white"}`}>
                            {opt}
                        </button>
                    ))}
                </div>
            ) : q.type === "percentage_allocation" ? (
                (() => {
                    const obj = (value && typeof value === "object") ? value : {};
                    const total = (q.options || []).reduce((s, opt) => s + (Number(obj[opt]) || 0), 0);
                    const ok = total === 100;
                    return (
                        <div className="space-y-2" data-testid={`percentage-allocation-${q.id}`}>
                            {(q.options || []).map((opt) => (
                                <div key={opt} className="flex items-center gap-2">
                                    <span className="flex-1 text-sm text-gray-300">{opt}</span>
                                    <input type="number" min={0} max={100} step={1}
                                        value={obj[opt] ?? ""}
                                        onChange={(e) => {
                                            const raw = e.target.value;
                                            const n = raw === "" ? "" : Math.max(0, Math.min(100, Math.floor(Number(raw) || 0)));
                                            onChange({ ...obj, [opt]: n });
                                        }}
                                        className="w-20 bg-muted border border-border/40 px-2 py-1.5 text-sm text-white text-right outline-none"
                                        data-testid={`pct-input-${q.id}-${opt.slice(0, 20).replace(/\s+/g, "-")}`} />
                                    <span className="text-xs text-gray-500 font-mono w-4">%</span>
                                </div>
                            ))}
                            <div className={`flex items-center justify-between border-t border-border/40 pt-2 mt-2 ${ok ? "text-emerald-300" : "text-red-300"}`}
                                data-testid={`pct-total-${q.id}`}>
                                <span className="text-xs uppercase tracking-wider font-mono">Total</span>
                                <span className="text-sm font-mono">
                                    {total}% {ok ? "✓" : "/ 100%"}
                                </span>
                            </div>
                            {!ok && (
                                <p className="text-xs text-red-300/80" data-testid={`pct-warning-${q.id}`}>
                                    Total must be 100%. You can still submit, but please double-check the allocation.
                                </p>
                            )}
                        </div>
                    );
                })()
            ) : q.type === "multi_select" ? (
                <div className="flex flex-wrap gap-2">
                    {(q.options || []).map((opt) => {
                        const arr = Array.isArray(value) ? value : [];
                        const on = arr.includes(opt);
                        return (
                            <button key={opt} onClick={() => onChange(on ? arr.filter((x) => x !== opt) : [...arr, opt])}
                                className={`px-3 py-1.5 text-xs border transition-colors ${on ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white"}`}>
                                {opt}
                            </button>
                        );
                    })}
                </div>
            ) : q.type === "rank" ? (
                <div className="space-y-2">
                    {(q.options || []).map((opt, i) => (
                        <div key={opt} className="flex items-center gap-2">
                            <input type="number" min={1} max={(q.options || []).length}
                                value={(value && value[opt]) || ""}
                                onChange={(e) => onChange({ ...(value || {}), [opt]: Number(e.target.value) })}
                                className="w-16 bg-muted border border-border/40 px-2 py-1 text-sm text-white outline-none" />
                            <span className="text-sm text-gray-300">{opt}</span>
                        </div>
                    ))}
                </div>
            ) : q.type === "rating_grid" ? (
                <div className="flex gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <button key={n} onClick={() => onChange(n)}
                            className={`w-9 h-9 text-xs border transition-colors ${value === n ? "bg-white text-black border-white" : "border-border/40 text-gray-400 hover:text-white"}`}>
                            {n}
                        </button>
                    ))}
                </div>
            ) : (
                <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className={`${common} h-20 resize-none`} placeholder="Your response..." />
            )}
        </div>
    );
}
